import React, { useState, useEffect } from "react";
import { Wizard } from "react-use-wizard";
import createPersistedState from "use-persisted-state";

// Components
import TextInputStep from "./TextInputStep";
import SingleChoiceStep from "./SingleChoiceStep";
import MultiChoiceStep from "./MultiChoiceStep";
import SubmitStep from "./SubmitStep";

import { useTranslation } from "react-i18next";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import IntroMessageStep from "./IntroMessageStep";

// Persisted state
const useTokenState = createPersistedState("token");
const usePIDState = createPersistedState("pid");

const DietInfoWizard = () => {
  const [token] = useTokenState();
  const [pid] = usePIDState();
  const [state, setState] = useState({});
  const location = useLocation();
  const history = useHistory();
  // Get the route we came from
  const rawFrom = new URLSearchParams(location.search).get("from");
  const fromRoute = rawFrom ? decodeURIComponent(rawFrom) : null;

  const { t } = useTranslation();

  const questionsData = {
    D1: {
      question:
        "What best describes the type of work or daily activity at your job?",
      options: {
        D1C1: "Sedentary",
        D1C2: "Light activity",
        D1C3: "Physically Demanding",
        D1C4: "Unemployed",
        D1C5: "Retired",
        D1C6: "Student",
        D1C7: "Homemaker",
      },
    },
    D2: {
      question: "Does your job has shift base work?",
      options: {
        D2C1: "Fixed Day Shift",
        D2C2: "Fixed Night Shift",
        D2C3: "Varies Rotationally",
        D2C4: "No fixed hours of working",
      },
    },
    D3: {
      question: "Do you have any of the following digestive problems?",
      options: {
        D3C1: "Bloating",
        D3C2: "Gas",
        D3C3: "Constipation",
        D3C4: "Acidity",
        D3C5: "None of these",
      },
    },
    D4: {
      question: "Are you allergic to any food?",
    },
    D5: {
      question: "Select if you Consume the following.",
      options: {
        D5C1: "Alcohol",
        D5C2: "Tobacco",
        D5C3: "Aerated/Cold drinks",
        D5C4: "None of these",
      },
    },
    D6: {
      question: "How fast do you chew food?",
      options: {
        D6C1: "Slow",
        D6C2: "Medium",
        D6C3: "Fast",
      },
    },
    D7: {
      question: "How much water do you drink every day?",
      options: {
        D7C1: "Less than 6 glasses",
        D7C2: "6 to 8 glasses",
        D7C3: "More than 8 glasses",
      },
    },

    D9: {
      question: "For women: Please provide menstrual information.",
      options: {
        D9C1: "Regular",
        D9C2: "Irregular",
        D9C3: "Not Applicable",
      },
    },
  };

  // Initial state structure
  const initialState = {
    D1: { _state: {} }, // Occupation
    D2: { _state: {} }, // Preferred name
    D3: { _state: {} }, // Digestive health
    D4: { _state: "" }, // Food allergies
    D5: { _state: {} }, // Substance consumption
    D6: { _state: {} }, // Chewing speed
    D7: { _state: {} }, // Water intake

    D9: { _state: {} }, // Menstrual cycle
  };

  useEffect(() => {
    fetchDietInfo();
  }, [pid, token]);

  const fetchDietInfo = async () => {
    try {
      const response = await fetch(`/api/v1/users/profile/${pid}/dietinfo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data?.status?.success && data?.dietInfo) {
        setState(processDietResponse(data.dietInfo));
      } else {
        setState(initialState);
      }
    } catch (error) {
      console.error("Error fetching diet info:", error);
      setState(initialState);
    }
  };

  const processDietResponse = (apiData) => {
    const newState = JSON.parse(JSON.stringify(initialState));

    apiData.forEach((item) => {
      if (!newState[item.code]) return;

      switch (item.type) {
        case "TEXT":
          newState[item.code]._state = item.response || "";
          break;
        case "RADIO":
          const selectedRadio = item.choices?.find((choice) => choice.correct);

          newState[item.code]._state = selectedRadio
            ? {
                checked: true,
                code: selectedRadio.code,
                text: selectedRadio.text,
                value: selectedRadio.text,
              }
            : {
                checked: false,
                code: null,
                text: null,
                value: null,
              };
          break;
        case "MULTI":
          item.choices?.forEach((choice) => {
            newState[item.code]._state[choice.code] = {
              checked: choice.correct || false,
              code: choice.code,
              text: choice.text,
              value: choice.text,
            };
          });
          break;
      }
    });

    return newState;
  };

  const hasOption = (optionCode, stepState) => {
    if (stepState["_state"]) {
      let selectedOptions = [];
      if (stepState["_state"].constructor === Object) {
        selectedOptions = stepState["_state"]; //TODO: Hack need to check this thoroughly
      } else {
        selectedOptions = stepState["_state"];
      }
      return (
        selectedOptions?.hasOwnProperty(optionCode) &&
        selectedOptions[optionCode]?.checked
      );
    } else {
      return false;
    }
  };

  const updateStatus = (deltaState) => {
    const stepKey = Object.keys(deltaState)[0];

    // if (stepKey === "D3" && hasOption("D3C5", deltaState["D3"])) {
    //   deltaState["D3"] = {};
    //   deltaState["D3"]["_state"] = null;
    // } else if (stepKey === "D5" && hasOption("D5C4", deltaState["D5"])) {
    //   deltaState["D5"] = {};
    //   deltaState["D5"]["_state"] = null;
    // }

    setState((prev) => ({
      ...prev,
      ...deltaState,
    }));
  };

  const getSubState = (qKey) => {
    return state[qKey]?._state;
  };

  // Helper to format choices for components
  const formatChoices = (questionCode) => {
    const options = questionsData[questionCode]?.options;
    if (!options) return [];

    return Object.entries(options).map(([code, text]) => ({
      code,
      text: t(`${questionCode}.options.${code}`),
      value: text,
      exclusive: code === "D3C5" || code === "D5C4", // Example of an exclusive option
    }));
  };

  return (
    <div className="diet-wizard">
      <Wizard>
        {/* Occupation */}
        <IntroMessageStep isFirstWizardStep={true} />
        <SingleChoiceStep
          qKey="D1"
          question={t("D1.question")}
          choices={formatChoices("D1")}
          initState={getSubState("D1")}
          onSubmitHandler={updateStatus}
          isFirstWizardStep={true}
        />

        {/* Preferred Name */}
        <SingleChoiceStep
          qKey="D2"
          question={t("D2.question")}
          choices={formatChoices("D2")}
          initState={getSubState("D2")}
          onSubmitHandler={updateStatus}
        />

        {/* Digestive Health */}
        <MultiChoiceStep
          qKey="D3"
          question={t("D3.question")}
          choices={formatChoices("D3")}
          initState={getSubState("D3")}
          onSubmitHandler={updateStatus}
          isPillView={false}
        />

        {/* Food Allergies */}
        <TextInputStep
          question={t("D4.question")}
          field="D4"
          initState={getSubState("D4")}
          onSubmitHandler={updateStatus}
        />

        {/* Substance Consumption */}
        <MultiChoiceStep
          qKey="D5"
          question={t("D5.question")}
          choices={formatChoices("D5")}
          initState={getSubState("D5")}
          onSubmitHandler={updateStatus}
          isPillView={true}
        />

        {/* Chewing Speed */}
        <SingleChoiceStep
          qKey="D6"
          question={t("D6.question")}
          choices={formatChoices("D6")}
          initState={getSubState("D6")}
          onSubmitHandler={updateStatus}
        />

        {/* Water Intake */}
        <SingleChoiceStep
          qKey="D7"
          question={t("D7.question")}
          choices={formatChoices("D7")}
          initState={getSubState("D7")}
          onSubmitHandler={updateStatus}
        />

        {/* Stress Level */}
        {/* <SingleChoiceStep
          qKey="D8"
          question={t("D8.question")}
          choices={formatChoices("D8")}
          initState={getSubState("D8")}
          onSubmitHandler={updateStatus}
        /> */}

        {/* Menstrual Cycle (conditional) */}
        <SingleChoiceStep
          qKey="D9"
          question={t("D9.question")}
          choices={formatChoices("D9")}
          initState={getSubState("D9")}
          onSubmitHandler={updateStatus}
        />

        {/* Submit Step */}
        <SubmitStep data={state} fromRoute={fromRoute} />
      </Wizard>
    </div>
  );
};

export default DietInfoWizard;
